import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Target, Plus, Trash2, CheckCircle, Circle, Calendar, Sparkles, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GOAL_CATEGORIES = [
  { id: 'inner', name: 'Ditt inre', color: 'bg-rose-100 text-rose-700', icon: '💗' },
  { id: 'body', name: 'Din kropp', color: 'bg-emerald-100 text-emerald-700', icon: '💪' },
  { id: 'mind', name: 'Ditt sinne', color: 'bg-violet-100 text-violet-700', icon: '🧠' },
  { id: 'relations', name: 'Dina relationer', color: 'bg-pink-100 text-pink-700', icon: '❤️' },
  { id: 'lifestyle', name: 'Din vardag', color: 'bg-sky-100 text-sky-700', icon: '🏠' },
];

const SUGGESTED_GOALS = {
  inner: ['Skriva dagbok 3 gånger i veckan', 'Öva tacksamhet varje dag', 'Reflektera 10 min dagligen'],
  body: ['Träna 3 gånger i veckan', 'Gå 8000 steg dagligen', 'Sova 7-8 timmar varje natt'],
  mind: ['Läsa 20 min varje dag', 'Meditera 5 min dagligen', 'Lära mig något nytt varje vecka'],
  relations: ['Ringa en vän varje vecka', 'Ha kvalitetstid med familjen', 'Säga nej när jag behöver'],
  lifestyle: ['Planera veckan på söndagar', 'Hålla budget varje månad', 'Skapa en morgonrutin'],
};

export default function GoalsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGoal, setNewGoal] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('inner');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const c = {
    back: 'Tillbaka',
    title: 'Mina mål',
    addGoal: 'Lägg till mål',
    placeholder: 'Skriv ditt mål...',
    suggestions: 'Förslag',
    noGoals: 'Inga mål ännu',
    noGoalsDesc: 'Sätt ditt första mål för att komma igång',
    goalAdded: 'Mål tillagt!',
    goalDeleted: 'Mål borttaget',
    completed: 'avklarade',
    goalsAchieved: 'mål uppnådda',
    showSuggestions: 'Visa förslag',
    hideSuggestions: 'Dölj förslag',
    congratulations: '🎉 Grattis! Du har uppnått ett mål!'
  };

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/api/goals`, getAuthHeader());
      setGoals(response.data.goals || []);
    } catch (error) {
      // If endpoint doesn't exist yet, use local storage
      const stored = localStorage.getItem('livon-goals');
      if (stored) {
        setGoals(JSON.parse(stored));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveGoals = async (updatedGoals) => {
    setGoals(updatedGoals);
    localStorage.setItem('livon-goals', JSON.stringify(updatedGoals));
    try {
      await axios.post(`${API}/api/goals`, { goals: updatedGoals }, getAuthHeader());
    } catch (error) {
      // Offline - already saved to localStorage
    }
  };

  const addGoal = () => {
    if (!newGoal.trim()) return;
    
    const goal = {
      id: Date.now().toString(),
      text: newGoal.trim(),
      category: selectedCategory,
      completed: false,
      createdAt: new Date().toISOString(),
      progress: 0
    };
    
    const updatedGoals = [...goals, goal];
    saveGoals(updatedGoals);
    setNewGoal('');
    toast.success(c.goalAdded);
  };

  const addSuggestedGoal = (text) => {
    const goal = {
      id: Date.now().toString(),
      text,
      category: selectedCategory,
      completed: false,
      createdAt: new Date().toISOString(),
      progress: 0
    };
    
    const updatedGoals = [...goals, goal];
    saveGoals(updatedGoals);
    setShowSuggestions(false);
    toast.success(c.goalAdded);
  };

  const toggleGoal = (goalId) => {
    const updatedGoals = goals.map(g => 
      g.id === goalId ? { ...g, completed: !g.completed, completedAt: !g.completed ? new Date().toISOString() : null } : g
    );
    saveGoals(updatedGoals);
    
    const goal = goals.find(g => g.id === goalId);
    if (!goal.completed) {
      toast.success(c.congratulations);
    }
  };

  const deleteGoal = (goalId) => {
    const updatedGoals = goals.filter(g => g.id !== goalId);
    saveGoals(updatedGoals);
    toast.success(c.goalDeleted);
  };

  const completedCount = goals.filter(g => g.completed).length;
  const totalCount = goals.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard', { state: { expandCategory: 'lifestyle' } })}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {c.back}
          </Button>
          <h1 className="font-heading text-xl">{c.title}</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Progress overview */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 text-center">
          <Trophy className="w-12 h-12 mx-auto text-primary mb-4" />
          <h2 className="font-heading text-3xl mb-2">{completedCount} / {totalCount}</h2>
          <p className="text-muted-foreground">{c.goalsAchieved}</p>
          {totalCount > 0 && (
            <div className="mt-4 bg-background/50 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* Category selector */}
        <div className="flex flex-wrap gap-2 justify-center">
          {GOAL_CATEGORIES.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className="rounded-full"
            >
              <span className="mr-2">{cat.icon}</span>
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Add new goal */}
        <div className="bg-card rounded-2xl p-6 space-y-4">
          <h3 className="font-heading text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            {c.addGoal}
          </h3>
          
          <div className="flex gap-3">
            <Input
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              placeholder={c.placeholder}
              className="flex-1 rounded-xl bg-secondary/50 border-0"
              onKeyPress={(e) => e.key === 'Enter' && addGoal()}
            />
            <Button onClick={addGoal} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              {c.addGoal}
            </Button>
          </div>

          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="text-primary"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {showSuggestions ? c.hideSuggestions : c.showSuggestions}
          </Button>

          {showSuggestions && (
            <div className="flex flex-wrap gap-2 pt-2">
              {SUGGESTED_GOALS[selectedCategory]?.map((suggestion, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => addSuggestedGoal(suggestion)}
                  className="rounded-full text-xs"
                >
                  + {suggestion}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Goals list */}
        <div className="space-y-4">
          {GOAL_CATEGORIES.map((cat) => {
            const categoryGoals = goals.filter(g => g.category === cat.id);
            if (categoryGoals.length === 0) return null;

            return (
              <div key={cat.id} className="bg-card rounded-2xl p-6 space-y-4">
                <h3 className="font-heading text-lg flex items-center gap-2">
                  <span>{cat.icon}</span>
                  {cat.name}
                  <span className="text-sm text-muted-foreground ml-auto">
                    {categoryGoals.filter(g => g.completed).length}/{categoryGoals.length}
                  </span>
                </h3>
                
                <div className="space-y-3">
                  {categoryGoals.map((goal) => (
                    <div 
                      key={goal.id}
                      className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                        goal.completed ? 'bg-primary/10' : 'bg-secondary/30'
                      }`}
                    >
                      <button onClick={() => toggleGoal(goal.id)}>
                        {goal.completed ? (
                          <CheckCircle className="w-6 h-6 text-primary" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground" />
                        )}
                      </button>
                      <span className={`flex-1 ${goal.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {goal.text}
                      </span>
                      <button 
                        onClick={() => deleteGoal(goal.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {goals.length === 0 && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{c.noGoals}</p>
              <p className="text-sm">{c.noGoalsDesc}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
